import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from '@/components/utils/translations';
import {
  fetchMarketAccountTechStack,
  fetchMarketLenses,
} from '@/lib/marketExperienceApi';
import { formatCategoryLabel } from '@/lib/marketExperience';
import {
  VENDOR_PREVIEW_LIMIT,
  categoryHeading,
  partitionStackByRole,
  prepareCategoryVendors,
  vendorProductLabel,
} from '@/lib/marketTechStack';

function marketAccountId(account) {
  const raw = account?.account_id || account?.id;
  if (!raw) return null;
  const s = String(raw);
  const tenant = account?.tenant_id;
  if (tenant && s.startsWith(`${tenant}_`)) return s.slice(tenant.length + 1);
  return s;
}

function CategoryBlock({ category, expanded, onToggle, tenantId }) {
  const heading = categoryHeading(category, tenantId) || (category?.category ? formatCategoryLabel(category.category) : null);
  const items = prepareCategoryVendors(category?.vendors)
    .map(vendorProductLabel)
    .filter(Boolean);
  if (!heading || !items.length) return null;

  const visible = expanded ? items : items.slice(0, VENDOR_PREVIEW_LIMIT);
  const extra = items.length - visible.length;

  return (
    <div>
      <p className="text-[10px] font-semibold tracking-wide text-[#5C6B7A]">{heading}</p>
      <ul className="mt-0.5">
        {visible.map((label, index) => (
          <li key={`${label}-${index}`} className="text-[11px] text-[#0A1A2F] leading-snug truncate">
            {label}
          </li>
        ))}
      </ul>
      {items.length > VENDOR_PREVIEW_LIMIT ? (
        <button
          type="button"
          onClick={onToggle}
          className="mt-0.5 text-[10px] text-[#00C2C2] hover:underline"
        >
          {expanded ? 'Show less' : `+${extra} more`}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Market-module tech stack: GET /api/market/accounts/:id/tech-stack.
 * Category + vendor/product names only.
 */
export default function MarketAccountTechStack({ account, currentTenant, onUnavailable, onBadges }) {
  const { t } = useTranslation();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState({});

  const tenantId = currentTenant || account?.tenant_id;
  const accountId = marketAccountId(account);

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !accountId) {
      setLoading(false);
      setFailed(true);
      onUnavailable?.();
      return undefined;
    }
    setLoading(true);
    setFailed(false);
    setExpanded({});
    (async () => {
      try {
        let lens;
        try {
          const lenses = await fetchMarketLenses(tenantId);
          lens = lenses?.default_lens || lenses?.lenses?.[0]?.lens_key;
        } catch {
          // Tenant session still scopes /tech-stack; lens is optional.
        }
        const data = await fetchMarketAccountTechStack(tenantId, accountId, lens);
        if (cancelled) return;
        setPayload(data);
        onBadges?.(data?.badges || null);
      } catch {
        if (cancelled) return;
        setFailed(true);
        onBadges?.(null);
        onUnavailable?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, accountId]);

  if (failed) return null;
  if (payload?.resolved === false) return null;

  if (loading) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-3 md:p-4">
          <p className="text-xs md:text-sm text-[#5C6B7A] text-center mb-2">{t('techStack')}</p>
          <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  const stack = Array.isArray(payload?.stack) ? payload.stack : [];
  const { lens, context } = partitionStackByRole(stack);
  const categories = [...lens, ...context];
  const hasStack = categories.length > 0;

  const toggle = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Card className="border-0 shadow-md">
      <CardContent className="p-3 md:p-4">
        <p className="text-xs md:text-sm text-[#5C6B7A] text-center mb-2">{t('techStack')}</p>
        {hasStack ? (
          <div className="space-y-2.5">
            {categories.map((cat, index) => (
              <CategoryBlock
                key={cat.category || cat.label || `cat-${index}`}
                category={cat}
                tenantId={tenantId}
                expanded={Boolean(expanded[cat.category || cat.label])}
                onToggle={() => toggle(cat.category || cat.label)}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-center text-[#5C6B7A]">{t('noTechData') || 'No specified tech data'}</p>
        )}
      </CardContent>
    </Card>
  );
}
